/**
 * Full Send — Inline Quick-Add with Variant Memory
 *
 * Owns:
 *   - VariantMemory: localStorage-backed preference store keyed by option name (lowercase)
 *   - QuickAdd: pill hydration, click delegation, cross-card sync, Add-to-cart, hint tooltip
 *
 * Exposes:
 *   window.FullSend.VariantMemory.get(optionName) -> string | null
 *   window.FullSend.VariantMemory.set(optionName, value)  // dispatches "fs:variantPrefs:change"
 *   window.FullSend.VariantMemory.clear(optionName)
 *   window.FullSend.QuickAdd.hydrate(rootElement?)         // pre-select pills from memory
 *   window.FullSend.QuickAdd.hydrateOne(quickAddElement)   // hydrate a single overlay
 *   window.FullSend.QuickAdd.observeForHint(rootElement?)  // register cards with hint observer
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'fs:variantPrefs';
  var CURRENT_VERSION = 1;

  // In-memory fallback when localStorage is unavailable (private mode, quota, etc.)
  var memoryFallback = { v: CURRENT_VERSION };
  var useFallback = false;

  function readPrefs() {
    if (useFallback) return memoryFallback;
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { v: CURRENT_VERSION };
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
      return { v: CURRENT_VERSION };
    } catch (e) {
      useFallback = true;
      return memoryFallback;
    }
  }

  function writePrefs(prefs) {
    prefs.v = CURRENT_VERSION;
    if (useFallback) {
      memoryFallback = prefs;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      useFallback = true;
      memoryFallback = prefs;
    }
  }

  function dispatchPrefsChange(optionName, value) {
    try {
      document.dispatchEvent(new CustomEvent('fs:variantPrefs:change', {
        detail: { optionName: optionName, value: value }
      }));
    } catch (e) {
      // IE fallback unnecessary — Shopify only supports evergreen browsers
    }
  }

  var VariantMemory = {
    get: function (optionName) {
      if (!optionName) return null;
      var key = String(optionName).toLowerCase();
      var prefs = readPrefs();
      return Object.prototype.hasOwnProperty.call(prefs, key) ? prefs[key] : null;
    },
    set: function (optionName, value) {
      if (!optionName) return;
      var key = String(optionName).toLowerCase();
      var prefs = readPrefs();
      prefs[key] = value;
      writePrefs(prefs);
      dispatchPrefsChange(key, value);
    },
    clear: function (optionName) {
      if (!optionName) return;
      var key = String(optionName).toLowerCase();
      var prefs = readPrefs();
      delete prefs[key];
      writePrefs(prefs);
      dispatchPrefsChange(key, null);
    }
  };

  function hydrateOne(quickAdd) {
    if (!quickAdd) return;
    // Skip re-hydrating cards the user has already interacted with on this page-load
    if (quickAdd.dataset.userInteracted === 'true') return;

    var pills = quickAdd.querySelectorAll('.product-card--v2__size-pill');
    var addBtn = quickAdd.querySelector('[data-quick-add-submit]');

    // No-pill overlay (single-variant product reusing the overlay UI). The
    // button is already enabled from Liquid; nothing to hydrate.
    if (pills.length === 0) return;

    var optionName = quickAdd.dataset.sizeOptionName || 'Size';
    var remembered = VariantMemory.get(optionName);
    var savedLabel = quickAdd.querySelector('[data-saved-label]');
    var matched = false;

    for (var i = 0; i < pills.length; i++) {
      var pill = pills[i];
      pill.classList.remove('is-selected');
      pill.setAttribute('aria-checked', 'false');
      pill.setAttribute('tabindex', '-1');

      if (
        !matched &&
        remembered &&
        !pill.disabled &&
        pill.dataset.size &&
        pill.dataset.size.toLowerCase() === String(remembered).toLowerCase()
      ) {
        pill.classList.add('is-selected');
        pill.setAttribute('aria-checked', 'true');
        pill.setAttribute('tabindex', '0');
        matched = true;
      }
    }

    // If nothing got selected, ensure the first enabled pill is keyboard-focusable
    if (!matched) {
      for (var j = 0; j < pills.length; j++) {
        if (!pills[j].disabled) {
          pills[j].setAttribute('tabindex', '0');
          break;
        }
      }
    }

    if (savedLabel) {
      if (matched) savedLabel.removeAttribute('hidden');
      else savedLabel.setAttribute('hidden', '');
    }

    if (addBtn) {
      if (matched) addBtn.removeAttribute('disabled');
      else addBtn.setAttribute('disabled', '');
    }
  }

  function hydrate(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var quickAdds = scope.querySelectorAll('[data-quick-add]');
    for (var i = 0; i < quickAdds.length; i++) {
      hydrateOne(quickAdds[i]);
    }
  }

  var HINT_FLAG_KEY = 'fs:quickAddHintSeen';
  var HINT_AUTO_DISMISS_MS = 4000;
  var hintShownThisSession = false;
  var hintActiveCard = null;
  var hintDismissTimer = null;
  var hintObserver = null;

  function isHintSeen() {
    try { return localStorage.getItem(HINT_FLAG_KEY) === '1'; }
    catch (e) { return hintShownThisSession; }
  }

  function markHintSeenPermanently() {
    try { localStorage.setItem(HINT_FLAG_KEY, '1'); }
    catch (e) { /* in-memory only; the page-scope flag below covers it */ }
    hintShownThisSession = true;
    hideHint();
    if (hintObserver) {
      hintObserver.disconnect();
      hintObserver = null;
    }
  }

  function hideHint() {
    if (hintDismissTimer) {
      window.clearTimeout(hintDismissTimer);
      hintDismissTimer = null;
    }
    if (hintActiveCard) {
      var tip = hintActiveCard.querySelector('.product-card--v2__quick-add-hint');
      if (tip && tip.parentNode) tip.parentNode.removeChild(tip);
      hintActiveCard = null;
    }
  }

  function showHintOnQuickAdd(quickAdd) {
    if (hintShownThisSession || isHintSeen()) return;
    if (!quickAdd || hintActiveCard) return;
    // The hint reads "Pick a size to quick add" — meaningless on overlays
    // that don't have size pills (single-variant products).
    if (quickAdd.querySelectorAll('.product-card--v2__size-pill').length === 0) return;

    var tip = document.createElement('div');
    tip.className = 'product-card--v2__quick-add-hint';
    tip.setAttribute('role', 'tooltip');
    tip.textContent = 'Pick a size to quick add';
    quickAdd.appendChild(tip);

    hintActiveCard = quickAdd;
    hintShownThisSession = true;
    hintDismissTimer = window.setTimeout(hideHint, HINT_AUTO_DISMISS_MS);
  }

  function attachHintTriggers() {
    if (isHintSeen()) return;

    // Desktop: first hover or focus on any [data-quick-add]
    document.addEventListener('mouseenter', function (e) {
      var qa = e.target && e.target.closest && e.target.closest('[data-quick-add]');
      if (qa) showHintOnQuickAdd(qa);
    }, true);

    document.addEventListener('focusin', function (e) {
      var qa = e.target && e.target.closest && e.target.closest('[data-quick-add]');
      if (qa) showHintOnQuickAdd(qa);
    });

    // Mobile: first [data-quick-add] crossing 50% visibility
    if ('IntersectionObserver' in window) {
      hintObserver = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            showHintOnQuickAdd(entry.target);
            hintObserver.unobserve(entry.target);
          }
        }
      }, { threshold: [0.5] });

      var nodes = document.querySelectorAll('[data-quick-add]');
      for (var j = 0; j < nodes.length; j++) hintObserver.observe(nodes[j]);
    }
  }

  function selectPill(pill) {
    var quickAdd = pill.closest('[data-quick-add]');
    if (!quickAdd) return;
    if (!pill.dataset.size) return;

    var pills = quickAdd.querySelectorAll('.product-card--v2__size-pill');
    for (var i = 0; i < pills.length; i++) {
      pills[i].classList.remove('is-selected');
      pills[i].setAttribute('aria-checked', 'false');
      pills[i].setAttribute('tabindex', '-1');
    }
    pill.classList.add('is-selected');
    pill.setAttribute('aria-checked', 'true');
    pill.setAttribute('tabindex', '0');

    var savedLabel = quickAdd.querySelector('[data-saved-label]');
    if (savedLabel) savedLabel.setAttribute('hidden', '');

    quickAdd.dataset.userInteracted = 'true';

    var addBtn = quickAdd.querySelector('[data-quick-add-submit]');
    if (addBtn) addBtn.removeAttribute('disabled');

    // Echo the selected variant's price into the CTA label so the button
    // reads "Add to Cart · $X" matching the size the user just picked.
    var label = addBtn ? addBtn.querySelector('[data-cta-label]') : null;
    if (label && pill.dataset.variantPrice) {
      label.textContent = 'Add to Cart \u00B7 ' + pill.dataset.variantPrice;
    }

    var optionName = quickAdd.dataset.sizeOptionName || 'Size';
    VariantMemory.set(optionName, pill.dataset.size);
    markHintSeenPermanently();
  }

  document.addEventListener('keydown', function (e) {
    var pill = e.target && e.target.closest && e.target.closest('.product-card--v2__size-pill');
    if (!pill) return;

    if (e.key === ' ' || e.key === 'Enter') {
      if (pill.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      selectPill(pill);
      pill.focus();
      return;
    }

    var isNext = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    var isPrev = e.key === 'ArrowLeft' || e.key === 'ArrowUp';
    if (!isNext && !isPrev) return;

    var quickAdd = pill.closest('[data-quick-add]');
    if (!quickAdd) return;
    var enabled = [];
    var allPills = quickAdd.querySelectorAll('.product-card--v2__size-pill');
    for (var i = 0; i < allPills.length; i++) {
      if (!allPills[i].disabled) enabled.push(allPills[i]);
    }
    if (enabled.length === 0) return;

    var idx = enabled.indexOf(pill);
    if (idx === -1) return;

    e.preventDefault();
    e.stopPropagation();
    var nextIdx;
    if (isNext) nextIdx = (idx + 1) % enabled.length;
    else nextIdx = (idx - 1 + enabled.length) % enabled.length;

    // Make next pill focusable, defocus current
    for (var k = 0; k < allPills.length; k++) {
      allPills[k].setAttribute('tabindex', '-1');
    }
    enabled[nextIdx].setAttribute('tabindex', '0');
    enabled[nextIdx].focus();
  });

  document.addEventListener('click', function (e) {
    var pill = e.target.closest && e.target.closest('.product-card--v2__size-pill');
    if (!pill) return;
    if (pill.disabled) return;
    // The pill lives inside an <a> (product link) — stop the navigation
    e.preventDefault();
    e.stopPropagation();
    selectPill(pill);
  });

  function setQuickAddOpen(card, open) {
    if (!card) return;
    var overlay = card.querySelector('[data-quick-add]');
    var toggle = card.querySelector('[data-quick-add-toggle]');
    if (overlay) overlay.classList.toggle('is-open', open);
    if (toggle) {
      toggle.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  function closeAllQuickAdds(exceptCard) {
    var openOverlays = document.querySelectorAll('.product-card--v2__quick-add.is-open');
    for (var i = 0; i < openOverlays.length; i++) {
      var card = openOverlays[i].closest('.product-card--v2');
      if (card && card !== exceptCard) setQuickAddOpen(card, false);
    }
  }

  // Toggle button: reveals/hides the size overlay on touch devices
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest && e.target.closest('[data-quick-add-toggle]');
    if (toggle) {
      e.preventDefault();
      e.stopPropagation();
      var card = toggle.closest('.product-card--v2');
      var willOpen = !toggle.classList.contains('is-open');
      closeAllQuickAdds(willOpen ? card : null);
      setQuickAddOpen(card, willOpen);
      markHintSeenPermanently();
      return;
    }

    // Outside-tap: close any open overlays unless the click is on a pill / submit / overlay itself
    var insideOverlay = e.target.closest && e.target.closest('[data-quick-add]');
    if (!insideOverlay) closeAllQuickAdds(null);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllQuickAdds(null);
  });

  function addToCart(quickAdd) {
    var addBtn = quickAdd.querySelector('[data-quick-add-submit]');
    if (!addBtn || addBtn.disabled || addBtn.dataset.loading === 'true') return;

    // Variant ID can come from a selected size pill OR — for single-variant
    // products that share this overlay for visual consistency — from the
    // submit button's own data-variant-id (set in Liquid at render time).
    var selectedPill = quickAdd.querySelector('.product-card--v2__size-pill.is-selected');
    var variantId = selectedPill ? selectedPill.dataset.variantId : addBtn.dataset.variantId;
    if (!variantId) return;
    var label = addBtn.querySelector('[data-cta-label]');
    var originalText = label ? label.textContent : '';

    addBtn.dataset.loading = 'true';
    if (label) label.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
      .then(function (r) {
        if (r.status === 422) throw new Error('sold-out');
        if (!r.ok) throw new Error('add-failed');
        return r.json();
      })
      .then(function (item) {
        addBtn.classList.add('is-success');
        if (label) label.textContent = 'Added!';
        document.dispatchEvent(new CustomEvent('cart:add', { detail: { item: item, silent: true } }));
        window.setTimeout(function () {
          addBtn.classList.remove('is-success');
          delete addBtn.dataset.loading;
          if (label) label.textContent = originalText;
        }, 1500);
      })
      .catch(function (err) {
        if (label) {
          label.textContent = err && err.message === 'sold-out' ? 'Sold out' : 'Error';
        }
        window.setTimeout(function () {
          delete addBtn.dataset.loading;
          if (label) label.textContent = originalText;
        }, 1500);
      });
  }

  document.addEventListener('click', function (e) {
    var addBtn = e.target.closest && e.target.closest('[data-quick-add-submit]');
    if (!addBtn) return;
    if (addBtn.disabled) return;
    e.preventDefault();
    e.stopPropagation();
    var quickAdd = addBtn.closest('[data-quick-add]');
    if (quickAdd) addToCart(quickAdd);
  });

  document.addEventListener('fs:variantPrefs:change', function (e) {
    var changedOption = e && e.detail && e.detail.optionName;
    var quickAdds = document.querySelectorAll('[data-quick-add]');
    for (var i = 0; i < quickAdds.length; i++) {
      var qa = quickAdds[i];
      if (qa.dataset.userInteracted === 'true') continue;
      // Only re-hydrate cards whose option name matches the change (lowercased compare)
      var qaOption = (qa.dataset.sizeOptionName || 'Size').toLowerCase();
      if (changedOption && qaOption !== changedOption) continue;
      hydrateOne(qa);
    }
  });

  function init() {
    hydrate();
    attachHintTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var QuickAdd = {
    hydrate: hydrate,
    hydrateOne: hydrateOne,
    observeForHint: function (root) {
      if (isHintSeen() || !hintObserver) return;
      var nodes = (root && root.querySelectorAll ? root : document).querySelectorAll('[data-quick-add]');
      for (var i = 0; i < nodes.length; i++) hintObserver.observe(nodes[i]);
    }
  };

  window.FullSend = window.FullSend || {};
  window.FullSend.VariantMemory = VariantMemory;
  window.FullSend.QuickAdd = QuickAdd;
})();
